import Crossword, {
  CrosswordGrid,
  CrosswordProvider,
  CrosswordProviderImperative,
  DirectionClues,
  ThemeProvider,
} from "@jaredreisinger/react-crossword";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Pagination,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import LayoutWithIcons from "./LayoutWithIcons";
import { useCharactersContext } from "../hooks/Context";

interface Question {
  updatedBy: string;
  question: string;
  mediaUrl: string;
  difficultyLevel: string;
  answerType: number;
  active: boolean;
  marks: number;
  updatedDate: string;
  subjectId: number;
  questionBankId: number;
  classId: number;
  createdDate: string;
  createdBy: string;
  optionsList: any[];
  questionPaperId: number;
  uiType: number;
  remarks: string;
}

type CrosswordEntry = {
  clue: string;
  answer: string;
  row: number;
  col: number;
};

type CrosswordDirection = {
  [number: number]: CrosswordEntry;
};

type CrosswordData = {
  across?: CrosswordDirection;
  down?: CrosswordDirection;
};

interface Layout10Props {
  Questions: Question[];
  sectionName: string;
}

const Layout10: React.FC<Layout10Props> = ({ Questions, sectionName }) => {
  // console.log("Crossword questions: ", Questions);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const crosswordRef = useRef<CrosswordProviderImperative>(null);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width:820px)");
  const currentQuestion = Questions[currentQuestionIndex];
  // console.log("Current Question: ", currentQuestion);
  //console.log("Current Question Index: ", currentQuestionIndex);
  const sentences = currentQuestion?.question.split("<br>");
  const [generatedLogs, setGeneratedLogs] = useState<string[]>([]);
  //let correctCount: number = generatedLogs.length;
  const [correctCount, setCorrectCount] = useState(generatedLogs.length);
  const [showAlert, setShowAlert] = useState(false);

  // Sync correctCount with generatedLogs length
  useEffect(() => {
    setCorrectCount(generatedLogs.length);
  }, [generatedLogs]);
  const handleAlertClose = () => setShowAlert(false);
  setTimeout(handleAlertClose, 3000);
  const mediaUrls = currentQuestion?.mediaUrl.split(",");
  // console.log("Media Urls: ", mediaUrls);

  const storageKey = `crossword-progress-${currentQuestion.questionBankId}`;
  //console.log(correctCount, "correctCount");
  function generateCrosswordProgressData(data: any) {
    const crosswordProgress: {
      date: number;
      guesses: { [key: string]: string };
    } = {
      date: Date.now(),
      guesses: {},
    };

    for (const direction of ["across", "down"]) {
      const directionData = data[direction];
      for (const number in directionData) {
        if (directionData.hasOwnProperty(number)) {
          const entry = directionData[number];
          const key = `${entry.row}_${entry.col}`;
          crosswordProgress.guesses[key] = entry.answer.charAt(0).toUpperCase();
        }
      }
    }

    return crosswordProgress;
  }

  function handleReset() {
    // console.log(crosswordRef.current?.isCrosswordCorrect());
    crosswordRef.current?.reset();
    setGeneratedLogs([]);
    //correctCount = 0;
    setCorrectCount(0);
  }

  function showCompleteAlert() {
    if (crosswordRef.current) {
      const isCorrect = crosswordRef.current.isCrosswordCorrect();
      if (isCorrect) {
        console.log("Crossword solved with correct values");
      } else {
        console.log("Crossword is not entirely correct, reset and try again");
      }
    }
  }

  const handleNavigate = (direction: "previous" | "next") => {
    if (direction === "previous") {
      setCurrentQuestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (direction === "next") {
      setCurrentQuestionIndex((prevIndex) =>
        Math.min(prevIndex + 1, Questions.length - 1)
      );
    }
  };

  const crosswordData1 = {
    across: {
      1: {
        clue: "They can rot your teeth id you have too many",
        answer: "CANDY",
        row: 6,
        col: 3,
      },
      2: {
        clue: "Use it in sandwiches",
        answer: "CHEESE",
        row: 3,
        col: 4,
      },
      3: {
        clue: "You have it on birthdays",
        answer: "CAKE",
        row: 1,
        col: 4,
      },
      4: {
        clue: "They are red and roundish in shape",
        answer: "APPLE",
        row: 5,
        col: 0,
      },
      5: {
        clue: "Pod shaped green vegetable that make you stronger",
        answer: "PEA",
        row: 2,
        col: 3,
      },
    },
    down: {
      6: {
        clue: "You can get it in many flavours",
        answer: "ICECREAM",
        row: 0,
        col: 4,
      },
      7: {
        clue: "Something eaten toasted and buttered",
        answer: "BREAD",
        row: 2,
        col: 0,
      },
    },
  };

  const crosswordData2 = {
    across: {
      1: {
        clue: "The bird often kept for their eggs",
        answer: "CHICKEN",
        row: 3,
        col: 1,
      },
      2: {
        clue: "A reptile with hardshell",
        answer: "TORTOISE",
        row: 4,
        col: 0,
      },
      3: {
        clue: "A male sheep",
        answer: "RAM",
        row: 2,
        col: 3,
      },
    },
    down: {
      4: {
        clue: "Christman tree",
        answer: "PINE",
        row: 1,
        col: 7,
      },
      5: {
        clue: "India's national bird",
        answer: "PEACOCK",
        row: 0,
        col: 4,
      },
      6: {
        clue: "Common farm animal",
        answer: "COW",
        row: 3,
        col: 1,
      },
    },
  };

  function parseCrosswordData(question: string): CrosswordData {
    const lines = question.split("<br>");
    // console.log("Lines:", lines);

    let acrossDirection: CrosswordDirection = {};
    let downDirection: CrosswordDirection = {};

    try {
      for (const line of lines) {
        // console.log("Line:", line);

        const match = line.match(
          /Direction: (\w+), Number: (\d+), Clue: (.*), Answer: (.*), Row: (\d+), Column: (\d+)/
        );

        if (match) {
          // console.log("Match:", match);
          const [, direction, numberStr, clue, answer, rowStr, colStr] = match;
          const number = parseInt(numberStr);
          const row = parseInt(rowStr);
          const col = parseInt(colStr);

          const entry: CrosswordEntry = {
            clue: clue.trim(),
            answer: answer.trim().replace(/\s+/g, ' ').trim().toUpperCase(), 
            row,
            col,
          };

          if (direction === "Across") {
            acrossDirection[number] = entry;
          } else if (direction === "Down") {
            downDirection[number] = entry;
          }
        }
      }
    } catch (error) {
      console.error("An error occurred while parsing crossword data:", error);
    }

    return {
      across: acrossDirection,
      down: downDirection,
    };
  }

  const parsedCrosswordData = parseCrosswordData(currentQuestion?.question);
  // console.log("parsed Data", parsedCrosswordData);
  const crosswordProgressData =
    generateCrosswordProgressData(parsedCrosswordData);
  // console.log("CrosswordprogressState", crosswordProgressData);

  const existingItem = localStorage.getItem(storageKey);

  if (!existingItem) {
    localStorage.setItem(storageKey, JSON.stringify(crosswordProgressData));
  }

  const transformedCrosswordData = {
    across: Object.fromEntries(
      Object.entries(parsedCrosswordData.across || {}).map(([key, value]) => [
        key.toString(),
        value,
      ])
    ),
    down: Object.fromEntries(
      Object.entries(parsedCrosswordData.down || {}).map(([key, value]) => [
        key.toString(),
        value,
      ])
    ),
  };

  // console.log("transformed Data", transformedCrosswordData);

  const handlePaginationChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentQuestionIndex(value - 1);
  };

  // const handleAnswerCorrect = (direction: any, number: any, answer: any) => {
  //   const logMessage = `Correctly answered ${direction} clue ${number} with answer ${answer}`;
  //   console.log(logMessage);
  //   setGeneratedLogs(logMessage);
  //   //correctCount = generatedLogs.length;
  //   setCorrectCount(generatedLogs.length);
  //   console.log("generatedLogs", generatedLogs);
  // };

  const handleAnswerCorrect = (direction: any, number: any, answer: any) => {
    const logMessage = `Correctly answered ${direction} clue ${number} with answer ${answer}`;

    setGeneratedLogs((prevLogs) => {
      // Check if the logMessage already exists in the array
      if (!prevLogs.includes(logMessage)) {
        // If it doesn't exist, add it to the array
        return [...prevLogs, logMessage];
      } else {
        // If it does exist, return the array as is
        return prevLogs;
      }
    });
    // Don't manually set correctCount here - let useEffect handle it
  };

  const [userInput, setUserInput] = useState<string[]>([]);

  let logMessage: string = "";
  const handleCellChange = (row: number, col: number, char: string) => {
    logMessage = `User entered '${char}' at row ${row}, column ${col}`;
    // console.log(logMessage, "User entered");

    // setUserInput((prevLogs) => {
    //   // Check if the logMessage already exists in the array
    //   if (!prevLogs.includes(logMessage)) {
    //     // If it doesn't exist, add it to the array
    //     return [...prevLogs, logMessage];
    //   } else {
    //     // If it does exist, return the array as is
    //     return prevLogs;
    //   }
    // });
  };

  // console.log("userInput", userInput);

  const {
    questionScores,
    setQuestionScores,
    isViewMode,
    examAnswers,
    setExamAnswers,
  } = useCharactersContext();

  const handleSubmission = () => {
    setShowAlert(true);
    
    if (crosswordRef.current) {
      showCompleteAlert(); 
    }
    
    if (correctCount > 0) {
      const questionIndex = questionScores.findIndex(
        (item) => item.questionBankId === currentQuestion.questionBankId
      );

      if (questionIndex !== -1) {
        // Update the existing score
        const updatedScores = [...questionScores];
        updatedScores[questionIndex] = {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          //score: currentQuestion.marks,
          score: correctCount,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        };
        // console.log("updatedScores ", updatedScores);

        setQuestionScores(updatedScores);
      } else {
        // Add a new score for the question
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            //score: currentQuestion.marks,
            score: correctCount,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      }
    } else {
      setQuestionScores((prevScores) => [
        ...prevScores,
        {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          score: 0,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        },
      ]);
    }

    const updatedExamAnswer = {
      questionBankId: currentQuestion.questionBankId,
      acutalOptionValue: generatedLogs
        .map((log) => log.trim().split(" ").pop())
        .join(),
      enteredOptionValue: generatedLogs
        .map((log) => log.trim().split(" ").pop())
        .join(),
    };

    // Check if there is already an answer for the current question
    const existingAnswerIndex = examAnswers.findIndex(
      (answer) => answer.questionBankId === currentQuestion.questionBankId
    );

    if (existingAnswerIndex !== -1) {
      // If an answer already exists, update the existing one
      setExamAnswers((prevAnswers) =>
        prevAnswers.map((answer, index) =>
          index === existingAnswerIndex ? { ...updatedExamAnswer } : answer
        )
      );
    } else {
      // If there is no existing answer, add the new answer
      setExamAnswers((prevAnswers) => [...prevAnswers, updatedExamAnswer]);
    }

    // console.log("updatedExamAnswer ", updatedExamAnswer);
    // console.log("examAnswers  ", examAnswers);
  };

  useEffect(() => {
    if (isViewMode) {
      if (crosswordRef.current) {
        crosswordRef.current.fillAllAnswers();
      }
    }
  }, [crosswordRef.current]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "auto",
        maxWidth: "100%",
        marginTop: isViewMode ? "3%" : isTablet ? "15%" : "auto",
      }}
    >
      {showAlert && (
        <Alert
          severity="success"
          variant="filled"
          sx={{
            position: "fixed",
            top: "30%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "0.5",
          }}
        >
          <AlertTitle sx={{ color: "white" }}>Answer submitted</AlertTitle>
        </Alert>
      )}
      {currentQuestion && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: "12px",
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
            pointerEvents: isViewMode ? "none" : "auto",
            // maxHeight: "60vh",
            p: "0",
          }}
        >
          <CardContent>
            <Box sx={{ justifyContent: "center" }}>
              <Typography
                sx={{
                  fontWeight: "700",
                  fontSize: isMobile ? "100%" : "110%",
                }}
              >
                {sentences[0]}
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 2,
              }}
            >
              <ThemeProvider
                theme={{
                  allowNonSquare: true,
                  cellBackground: "#69b7ff1a",
                  gridBackground: "#FFFFFF",
                  cellBorder: "#000000",
                  numberColor: "#E12027",
                  focusBackground: "#E12027",
                  highlightBackground: "#ba828c",
                  // columnBreakpoint: "900px",
                  // textColor: "#fff",
                }}
              >
                {/* <Crossword
                    data={transformedCrosswordData}
                    ref={crosswordRef}
                    onCrosswordComplete={showAlert}
                    useStorage={true}
                    storageKey={storageKey}
                  /> */}
                <CrosswordProvider
                  data={transformedCrosswordData}
                  ref={crosswordRef}
                  onCrosswordComplete={showCompleteAlert}
                  useStorage={true}
                  storageKey={storageKey}
                  onAnswerCorrect={handleAnswerCorrect}
                  onCellChange={handleCellChange}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      gap: "2em",
                    }}
                  >
                    {isMobile && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "80%",
                          }}
                        >
                          {mediaUrls[0] && (
                            <img
                              src={mediaUrls[0]}
                              style={{
                                width: "2em",
                                height: "2em",
                              }}
                            />
                          )}
                          {mediaUrls[1] && (
                            <img
                              src={mediaUrls[1]}
                              style={{
                                width: "2em",
                                height: "2em",
                                marginLeft: "90%",
                              }}
                            />
                          )}
                        </div>

                        <div
                          style={{
                            width: "90%",
                            height: "90%",
                            marginLeft: "auto",
                            marginRight: "auto",
                          }}
                        >
                          <CrosswordGrid />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "80%",
                          }}
                        >
                          {mediaUrls[2] && (
                            <img
                              src={mediaUrls[2]}
                              style={{
                                width: "2em",
                                height: "2em",
                              }}
                            />
                          )}
                          {mediaUrls[3] && (
                            <img
                              src={mediaUrls[3]}
                              style={{
                                width: "2em",
                                height: "2em",
                                marginLeft: "90%",
                              }}
                            />
                          )}
                        </div>
                      </>
                    )}
                    <div
                      style={{
                        width: isMobile ? "100%" : "60%",
                        height: "50%",
                      }}
                    >
                      <DirectionClues direction="across" />
                    </div>

                    <div
                      style={{
                        width: isMobile ? "100%" : "60%",
                        height: "50%",
                      }}
                    >
                      <DirectionClues direction="down" />
                    </div>
                    {!isMobile && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          {mediaUrls[0] && (
                            <img
                              src={mediaUrls[0]}
                              style={{
                                width: "2em",
                                height: "2em",
                              }}
                            />
                          )}
                          {mediaUrls[1] && (
                            <img
                              src={mediaUrls[1]}
                              style={{
                                width: "2em",
                                height: "2em",
                                // marginTop: "auto",
                              }}
                            />
                          )}
                        </div>
                        <div style={{ width: "95%", height: "90%" }}>
                          <CrosswordGrid />
                        </div>
                        {mediaUrls[2] && (
                          <img
                            src={mediaUrls[2]}
                            style={{
                              width: "2em",
                              height: "2em",
                            }}
                          />
                        )}
                        {mediaUrls[3] && (
                          <img
                            src={mediaUrls[3]}
                            style={{
                              width: "2em",
                              height: "2em",
                              top: "90%",
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                </CrosswordProvider>
              </ThemeProvider>
            </Box>
            {!isViewMode && (
              <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                <Button variant="outlined" onClick={handleReset}>
                  Reset
                </Button>
                <Button
                  onClick={handleSubmission}
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      <Grid container lg={12} md={12} sm={12} xs={12} justifyContent="center">
        <Pagination
          count={Questions.length}
          shape="rounded"
          sx={{
            marginBottom: "1vh",
            paddingTop: "4%",
          }}
          page={currentQuestionIndex + 1}
          onChange={handlePaginationChange}
        />
      </Grid>
      {/* </LayoutWithIcons> */}
    </div>
  );
};

export default Layout10;
